const PI = Math.PI.toFixed(8)
const W = (0.1).toFixed(4) // GRASS_WIDTH
const H = (0.3).toFixed(4) // GRASS_HEIGHT

// Everything but the blade alpha is per instance/vertex: blades are a few pixels
// wide but stacked deep, so per-fragment work multiplies with overdraw.
export const grassVert = /* glsl */ `
attribute vec4 aInstanceData; // x, z, rotation, heightScale

uniform sampler2D uGroundTex;
uniform float uCenterOffset;
uniform float uSide;
uniform float uTime;
uniform float uWindSpeed;
uniform vec2 uWindDirection;
uniform float uWindDivergence;
uniform float uWindStrength;
uniform float uReceiveShadow;

#include <shadowmap_pars_vertex>

#if defined( USE_SHADOWMAP ) && NUM_DIR_LIGHT_SHADOWS > 0 && defined( SHADOWMAP_TYPE_PCF )
  #define GRASS_SHADOW
  uniform sampler2DShadow directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];
#endif

varying vec2 vUv;
varying float vLight;
varying vec3 vGroundColor;
varying float vTerrainBlend;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 19.19);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i + vec2(0,0)), hash(i + vec2(1,0)), u.x),
    mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), u.x),
    u.y
  );
}

vec2 rotate2D(vec2 v, float a) {
  float s = sin(a); float c = cos(a);
  return vec2(v.x * c - v.y * s, v.x * s + v.y * c);
}

vec3 srgbToLinear(vec3 color) {
  bvec3 cutoff = lessThanEqual(color, vec3(0.04045));
  vec3 lower = color / 12.92;
  vec3 higher = pow((color + 0.055) / 1.055, vec3(2.4));
  return mix(higher, lower, vec3(cutoff));
}

void main() {
  vec2 wPos = aInstanceData.xy;
  float rot = aInstanceData.z;
  float heightScale = aInstanceData.w;

  float heightT = uv.y;
  vUv = uv;

  // Fully faded into the ground: collapse the blade so it costs no fragments
  float cameraDist = distance(cameraPosition.xz, wPos);
  vTerrainBlend = smoothstep(18.0, 42.0, cameraDist);
  float visible = step(vTerrainBlend, 0.999);

  // Dual-noise divergence wind mask
  vec2 dirA = rotate2D(uWindDirection,  uWindDivergence);
  vec2 dirB = rotate2D(uWindDirection, -uWindDivergence);
  float speedB = uWindSpeed * ${PI};
  float scaleB = 0.15 * ${PI};
  float noiseA = noise(wPos * 0.15  + dirA * uTime * uWindSpeed);
  float noiseB = noise(wPos * scaleB + dirB * uTime * speedB);
  float windMask = clamp(noiseA * noiseB, 0.0, 1.0);

  // Rotation axis perpendicular to wind direction in XZ plane
  vec3 windDir3 = normalize(vec3(uWindDirection.x, 0.0, uWindDirection.y));
  vec3 rotAxis  = normalize(vec3(-windDir3.z, 0.0, windDir3.x));
  float angle   = windMask * uWindStrength * heightT * heightT;
  float sa = sin(angle); float ca = cos(angle);

  float s = sin(rot);
  float c2 = cos(rot);
  vec3 pos = vec3(position.x * ${W}, position.y * ${H} * heightScale, position.z * ${W});
  vec3 rotPos = vec3(
    pos.x * c2 - pos.z * s,
    pos.y,
    pos.x * s + pos.z * c2
  );
  // Apply wind via Rodrigues rotation
  rotPos = rotPos * ca + cross(rotAxis, rotPos) * sa + rotAxis * dot(rotAxis, rotPos) * (1.0 - ca);
  rotPos *= visible;

  vec3 worldPos = rotPos + vec3(wPos.x, 0.0, wPos.y);

  vec4 worldPosition = vec4(worldPos, 1.0);
  #include <shadowmap_vertex>

  // Single hardware-PCF tap per vertex instead of 5 per fragment
  float shadowFactor = 1.0;
  #ifdef GRASS_SHADOW
    vec4 shadowCoord = vDirectionalShadowCoord[ 0 ];
    shadowCoord.xyz /= shadowCoord.w;
    shadowCoord.z += directionalLightShadows[ 0 ].shadowBias;
    bool inFrustum = all(greaterThanEqual(shadowCoord.xy, vec2(0.0)))
      && all(lessThanEqual(shadowCoord.xy, vec2(1.0)))
      && shadowCoord.z <= 1.0;
    if (inFrustum) {
      float lit = texture(directionalShadowMap[ 0 ], shadowCoord.xyz);
      shadowFactor = mix(1.0, lit, directionalLightShadows[ 0 ].shadowIntensity);
    }
    shadowFactor = mix(1.0, shadowFactor, uReceiveShadow);
  #endif
  vLight = mix(0.2, 1.0, shadowFactor) * 3.0;

  vec2 groundUv = vec2((wPos.x - uCenterOffset) / uSide + 0.5, 0.5 - wPos.y / uSide);
  vGroundColor = srgbToLinear(texture2D(uGroundTex, clamp(groundUv, 0.0, 1.0)).rgb);

  gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
}
`

export const grassFrag = /* glsl */ `
uniform sampler2D uGrassTex;

varying vec2 vUv;
varying float vLight;
varying vec3 vGroundColor;
varying float vTerrainBlend;

void main() {
  vec4 tex = texture2D(uGrassTex, vec2(vUv.x, 1.0 - vUv.y));
  if (tex.a < 0.35) discard;

  vec3 base = vec3(0.04, 0.22, 0.02);
  vec3 tip  = vec3(0.18, 0.48, 0.06);
  vec3 tint = mix(base, tip, vUv.y) * mix(0.5, 1.0, vUv.y);

  vec3 grassColor = tex.rgb * tint * vLight;
  gl_FragColor = vec4(mix(grassColor, vGroundColor, vTerrainBlend), 1.0);
}
`
